import type { ChannelId, ChannelResult } from '../channels/types.ts';
/**
 * The bench's local (deterministic) pass, INV-TR-01…07 with the seed's scope (ENGINE-SPEC §9.1, §9.2; DESIGN ED-8).
 *
 * Order, verbatim from the seed: phrase → lex → scaffold → dash → pipe (a–f) → quotes (default off) → tidy, with `caseAdapt`
 * and the changelog labels. The whole text is eligible, headings and quotations included; `pipe` strips Markdown residue in
 * every format.
 *
 * The phrase stage is ONE alternation of the 37 PHRASE_SWAPS in array order, the lex stage ONE alternation of the 87
 * LEX_SWAPS, longest key first. The seed applies them one regex at a time on the previous output; the output here is equal
 * because `interactions.ts` splits a stage into ordered sub-passes wherever two patterns can overlap or create matches for
 * each other (T-PROP-13 compares this file with the seed oracle on random and real text). Every other stage is already a
 * single regex per step.
 */
import { loadEnglishLexicons } from '../lang/en/lexicons/index.ts';
import { planStage } from './interactions.ts';
import type { StagePlan, SwapPattern } from './interactions.ts';

export interface LocalPassOptions {
	phrase?: boolean;
	lex?: boolean;
	scaffold?: boolean;
	dash?: boolean;
	pipe?: boolean;
	quotes?: boolean;
}

/** The seed's `benchOpts()` defaults: all on except `quotes`. */
export const DEFAULT_LOCAL_OPTIONS: Readonly<Required<LocalPassOptions>> = {
	phrase: true,
	lex: true,
	scaffold: true,
	dash: true,
	pipe: true,
	quotes: false,
};

export interface LocalPassResult {
	text: string;
	/** Changelog: label → count, in the seed's insertion order. */
	log: Record<string, number>;
}

export type Transform = 'phrase' | 'lex' | 'scaffold' | 'dash' | 'pipe' | 'quotes';

/** ED-9: the channel each transform is expected to move. */
export const TRANSFORM_CHANNELS: Readonly<Record<Transform, readonly ChannelId[]>> = {
	phrase: ['LX-02'],
	lex: ['LX-01'],
	scaffold: ['DS-01'],
	dash: ['PT-01', 'PROC-05'],
	pipe: ['OB-01'],
	quotes: [],
};

const escRe = (s: string): string => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const flexSpace = (s: string): string => escRe(s).replace(/\s+/g, '\\s+');

/** Seed `caseAdapt`: an empty replacement passes through; a capitalised match capitalises the replacement. */
export function caseAdapt(orig: string, repl: string): string {
	if (!repl) return repl;
	if (orig[0] === (orig[0] as string).toUpperCase())
		return (repl[0] as string).toUpperCase() + repl.slice(1);
	return repl;
}

/* ---------------------------------------------------------------- swap stages */

interface CompiledGroup {
	start: number;
	end: number;
	/** Group regex; alternative i is capture group i + 1 (a lone pattern has no capture group). */
	re: RegExp;
}

interface CompiledStage {
	patterns: SwapPattern[];
	labels: string[];
	plan: StagePlan;
	groups: CompiledGroup[];
}

function compileStage(
	patterns: SwapPattern[],
	source: (p: SwapPattern) => string,
	label: (p: SwapPattern) => string,
	wordBoundary: boolean
): CompiledStage {
	const plan = planStage({ patterns, wordBoundary });
	const groups: CompiledGroup[] = plan.groups.map(([start, end]) => {
		const srcs = patterns.slice(start, end).map(source);
		const body = srcs.length === 1 ? (srcs[0] as string) : srcs.map((s) => `(${s})`).join('|');
		return { start, end, re: new RegExp(body, 'gi') };
	});
	return { patterns, labels: patterns.map(label), plan, groups };
}

let phraseStage: CompiledStage | undefined;
let lexStage: CompiledStage | undefined;

/** The phrase stage: PHRASE_SWAPS in array order, no word boundary (INV-TR-01). */
export function phraseStagePlan(): CompiledStage {
	phraseStage ??= compileStage(
		loadEnglishLexicons().phraseSwaps.entries.map(([from, to]) => ({ from, to })),
		(p) => flexSpace(p.from),
		(p) => `phrase “${p.from.trim()}”`,
		false
	);
	return phraseStage;
}

/** The lex stage: LEX_SWAPS keys sorted longest first (a stable sort, as V8's), each wrapped in `\b` (INV-TR-02). */
export function lexStagePlan(): CompiledStage {
	if (!lexStage) {
		const entries = loadEnglishLexicons().lexSwaps.entries.map(([from, to]) => ({ from, to }));
		entries.sort((a, b) => b.from.length - a.from.length);
		lexStage = compileStage(
			entries,
			(p) => `\\b${flexSpace(p.from)}\\b`,
			(p) => `${p.from} → ${p.to}`,
			true
		);
	}
	return lexStage;
}

/** Run a swap stage: one alternation scan per sub-pass. Counts are per pattern; the log follows pattern order. */
function runStage(stage: CompiledStage, text: string, log: Record<string, number>): string {
	const counts = new Array<number>(stage.patterns.length).fill(0);
	let t = text;
	for (const g of stage.groups) {
		const { start, end, re } = g;
		if (end - start === 1) {
			const to = (stage.patterns[start] as SwapPattern).to;
			t = t.replace(re, (m: string) => {
				counts[start] = (counts[start] as number) + 1;
				return caseAdapt(m, to);
			});
			continue;
		}
		t = t.replace(re, (m: string, ...rest: unknown[]) => {
			for (let i = 0; i < end - start; i++) {
				if (rest[i] !== undefined) {
					const idx = start + i;
					counts[idx] = (counts[idx] as number) + 1;
					return caseAdapt(m, (stage.patterns[idx] as SwapPattern).to);
				}
			}
			return m;
		});
	}
	for (let i = 0; i < counts.length; i++) {
		const n = counts[i] as number;
		if (n > 0) {
			const key = stage.labels[i] as string;
			log[key] = (log[key] ?? 0) + n;
		}
	}
	return t;
}

/* ---------------------------------------------------------------- other stages */

let scaffoldRe: RegExp | undefined;
function scaffoldRegex(): RegExp {
	if (!scaffoldRe) {
		const { source, flags } = loadEnglishLexicons().scaffoldRe.re;
		scaffoldRe = new RegExp(source, flags);
	}
	return scaffoldRe;
}

const DUP_EXCLUDED = new Set(['more', 'less', 'over', 'again', 'time', 'round', 'side', 'half']);

/** The seed's `localPass` (INV-TR-01…07); `opts` is read as the seed reads it. */
export function localPass(
	text: string,
	opts: LocalPassOptions = DEFAULT_LOCAL_OPTIONS
): LocalPassResult {
	const log: Record<string, number> = {};
	const bump = (k: string, n = 1) => {
		log[k] = (log[k] ?? 0) + n;
	};
	let t = text;

	if (opts.phrase) t = runStage(phraseStagePlan(), t, log);
	if (opts.lex) t = runStage(lexStagePlan(), t, log);
	if (opts.scaffold) {
		t = t.replace(scaffoldRegex(), (_m: string, pre: string, word: string) => {
			bump(`de-scaffold "${word}"`);
			return pre;
		});
	}
	if (opts.dash) {
		t = t.replace(/\s*[—–]\s*/g, () => {
			bump('em/en dash removed');
			return ', ';
		});
		t = t
			.replace(/,\s*,+/g, ', ')
			.replace(/,\s*([.!?;:])/g, '$1')
			.replace(/\(\s*,\s*/g, '(')
			.replace(/\s*,\s*\)/g, ')');
	}
	if (opts.pipe) {
		t = t.replace(/\*\*([^*\n]{2,60})\*\*/g, (_m: string, a: string) => {
			bump('markdown bold stripped');
			return a;
		});
		t = t.replace(/__([^_\n]{2,40})__/g, (_m: string, a: string) => {
			bump('markdown underline stripped');
			return a;
		});
		t = t.replace(/(^|\n)#{2,3}\s+/g, (_m: string, a: string) => {
			bump('header hashes stripped');
			return a;
		});
		t = t.replace(/\s\[\d{1,2}\]/g, () => {
			bump('citation bracket removed');
			return '';
		});
		t = t.replace(/```[a-z]*\n?/g, () => {
			bump('code fence removed');
			return '';
		});
		t = t.replace(/\b(\w{4,})\s+and\s+\1\b/gi, (m: string, a: string) => {
			if (DUP_EXCLUDED.has(a.toLowerCase())) return m;
			bump(`duplication "${a} and ${a}" collapsed`);
			return a;
		});
	}
	if (opts.quotes) {
		const n = (t.match(/[“”‘’]/g) ?? []).length;
		if (n) bump('quotes straightened', n);
		t = t.replace(/[“”]/g, '"').replace(/[‘’]/g, "'");
	}
	// tidy: capitalise sentence starts orphaned by deletions, collapse spaces (always runs)
	t = t.replace(/ {2,}/g, ' ');
	t = t.replace(
		/(^|[.!?…]["”]?\s+)([a-z])/g,
		(_m: string, a: string, b: string) => a + b.toUpperCase()
	);
	t = t.replace(/(\n\s*)([a-z])/g, (_m: string, a: string, b: string) => a + b.toUpperCase());
	return { text: t.trim(), log };
}

/* ---------------------------------------------------------------- effect report (ED-9) */

export interface LocalEffectLine {
	transform: Transform;
	channel: ChannelId;
	before: number;
	after: number;
	/** True when the channel's score fell. */
	reduced: boolean;
}

export interface LocalEffectReport {
	lines: LocalEffectLine[];
	/** "reduced ‹channel› from X to Y" for each reduced channel. */
	reduced: string[];
	/** Channels not reduced (or not measurable): reported with the failure text of ED-9. */
	failed: ChannelId[];
	/** The exact failure text, or null when every targeted channel was reduced. */
	failure: string | null;
	/** All report lines: the reductions, then the failure text. */
	messages: string[];
}

/** ED-9 failure text, exactly. */
export const failureText = (channels: readonly string[]): string =>
	`Rewrite attempt failed to address: ${channels.join(', ')}`;

interface HasResults {
	byId: Partial<Record<ChannelId, ChannelResult>>;
}

/**
 * Effect report of a local pass: the targeted channels of every enabled transform, measured on the analysis before and after.
 * A channel counts as targeted when its transform ran and it was scored before. It is reduced when its score fell.
 */
export function localPassEffect(
	opts: LocalPassOptions,
	before: HasResults,
	after: HasResults
): LocalEffectReport {
	const lines: LocalEffectLine[] = [];
	const failed: ChannelId[] = [];
	const seen = new Set<ChannelId>();
	for (const tr of Object.keys(TRANSFORM_CHANNELS) as Transform[]) {
		if (!opts[tr]) continue;
		for (const ch of TRANSFORM_CHANNELS[tr]) {
			if (seen.has(ch)) continue;
			const b = before.byId[ch];
			if (!b || b.status !== 'scored') continue;
			seen.add(ch);
			const a = after.byId[ch];
			const bs = Math.round(b.doc.score);
			if (!a || a.status !== 'scored') {
				failed.push(ch);
				continue;
			}
			const as = Math.round(a.doc.score);
			const reduced = a.doc.score < b.doc.score && as < bs;
			lines.push({ transform: tr, channel: ch, before: bs, after: as, reduced });
			if (!reduced) failed.push(ch);
		}
	}
	const reduced = lines
		.filter((l) => l.reduced)
		.map((l) => `reduced ${l.channel} from ${l.before} to ${l.after}`);
	const failure = failed.length ? failureText(failed) : null;
	return { lines, reduced, failed, failure, messages: failure ? [...reduced, failure] : reduced };
}
